import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { api } from "./lib/api";
import AuthPage from "./pages/AuthPage";
import AuthCallback from "./pages/AuthCallback";
import Dashboard from "./pages/Dashboard";
import Pricing from "./pages/Pricing";
import Terms from "./pages/Terms";
import Refund from "./pages/Refund";
import Privacy from "./pages/Privacy";
import Referral from "./pages/Referral";
import Admin from "./pages/Admin";
import Wins from "./pages/Wins";
import About from "./pages/About";
import Backtest from "./pages/Backtest";
import Feedback from "./pages/Feedback";
import Roadmap from "./pages/Roadmap";
import RiskCalculator from "./pages/RiskCalculator";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import AdminBlog from "./pages/AdminBlog";
import AdminTestimonials from "./pages/AdminTestimonials";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import { Toaster } from "sonner";
import "./App.css";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-app">
        <div className="text-xs font-mono uppercase tracking-widest text-muted-fg animate-pulse">
          /// Loading…
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  // Track every visit (anonymous + logged-in) once per page load so the
  // dashboard "Live Visitors" widget reflects total site traffic — not just
  // dashboard users. Endpoint is idempotent per (IP, day), so multiple pings
  // from the same visitor in a day are deduped server-side.
  React.useEffect(() => {
    api.post("/track/visit").catch(() => {});
  }, []);
  // Handle Google OAuth callback fragment synchronously
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<AuthPage />} />
      <Route path="/wins" element={<Wins />} />
      <Route
        path="/dashboard"
        element={
          <Protected>
            <Dashboard />
          </Protected>
        }
      />
      <Route
        path="/pricing"
        element={
          <Protected>
            <Pricing />
          </Protected>
        }
      />
      <Route
        path="/referral"
        element={
          <Protected>
            <Referral />
          </Protected>
        }
      />
      <Route
        path="/admin"
        element={
          <Protected>
            <Admin />
          </Protected>
        }
      />
      <Route path="/register" element={<AuthPage />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/refund" element={<Refund />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/about" element={<About />} />
      <Route path="/backtest" element={<Protected><Backtest /></Protected>} />
      <Route path="/feedback" element={<Protected><Feedback /></Protected>} />
      <Route path="/roadmap" element={<Roadmap />} />
      <Route path="/risk-calculator" element={<Protected><RiskCalculator /></Protected>} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/admin/blog" element={<Protected><AdminBlog /></Protected>} />
      <Route path="/admin/testimonials" element={<Protected><AdminTestimonials /></Protected>} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <AppRouter />
            <Toaster position="bottom-right" theme="system" richColors />
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}
